// src/services/groupTripsApi.ts
// Group Trips API Service

import { api } from '../utils/api';
import type {
  TripMember,
  InviteRequest,
  InviteResult,
  InviteDetails,
  ChatMessage,
  SendMessageRequest,
  Poll,
  CreatePollRequest,
  CastVoteRequest,
  TripActivity,
  MemberOptout,
  MemberRole,
} from '../types/groupTrips';

// ====================================
// Member Management
// ====================================

export async function getTripMembers(tripId: string): Promise<TripMember[]> {
  const response = await api.get(`/trips/${tripId}/members`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch members');
  return data.members;
}

export async function inviteMembers(
  tripId: string,
  invites: InviteRequest[]
): Promise<InviteResult[]> {
  const response = await api.post(`/trips/${tripId}/members`, { invites });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to send invites');
  return data.results;
}

export async function getInviteDetails(token: string): Promise<InviteDetails> {
  const response = await api.get(`/trips/join/${token}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Invalid invitation');
  return data.invite;
}

export async function acceptInvite(token: string): Promise<{ tripId: string; tripTitle: string }> {
  const response = await api.post(`/trips/join/${token}`, {});
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to accept invitation');
  return { tripId: data.tripId, tripTitle: data.tripTitle };
}

export async function declineInvite(token: string): Promise<void> {
  const response = await api.post(`/trips/join/${token}/decline`, {});
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to decline invitation');
}

export async function updateMemberRsvp(
  tripId: string,
  memberId: string,
  status: 'accepted' | 'declined'
): Promise<void> {
  const response = await api.put(`/trips/${tripId}/members/${memberId}/rsvp`, { status });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to update RSVP');
}

export async function updateMemberRole(
  tripId: string,
  memberId: string,
  role: MemberRole
): Promise<void> {
  const response = await api.put(`/trips/${tripId}/members/${memberId}`, { role });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to update role');
}

export async function removeMember(tripId: string, memberId: string): Promise<void> {
  const response = await api.delete(`/trips/${tripId}/members/${memberId}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to remove member');
}

// ====================================
// Chat Messages
// ====================================

export async function getMessages(
  tripId: string,
  options?: { limit?: number; before?: string; after?: string }
): Promise<{ messages: ChatMessage[]; hasMore: boolean; cursor?: string }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.before) params.set('before', options.before);
  if (options?.after) params.set('after', options.after);

  const endpoint = `/trips/${tripId}/messages${params.toString() ? '?' + params : ''}`;
  const response = await api.get(endpoint);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch messages');
  return { messages: data.messages, hasMore: data.hasMore, cursor: data.cursor };
}

export async function sendMessage(
  tripId: string,
  request: SendMessageRequest
): Promise<ChatMessage> {
  const response = await api.post(`/trips/${tripId}/messages`, request);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to send message');
  return data.message;
}

export async function editMessage(
  tripId: string,
  messageId: string,
  text: string
): Promise<void> {
  const response = await api.put(`/trips/${tripId}/messages/${messageId}`, { text });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to edit message');
}

export async function deleteMessage(tripId: string, messageId: string): Promise<void> {
  const response = await api.delete(`/trips/${tripId}/messages/${messageId}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete message');
}

export async function getMessageReplies(
  tripId: string,
  messageId: string
): Promise<ChatMessage[]> {
  const response = await api.get(`/trips/${tripId}/messages/${messageId}/replies`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch replies');
  return data.replies;
}

// ====================================
// Polls
// ====================================

export async function getPolls(tripId: string): Promise<Poll[]> {
  const response = await api.get(`/trips/${tripId}/polls`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch polls');
  return data.polls;
}

export async function createPoll(
  tripId: string,
  request: CreatePollRequest
): Promise<string> {
  const response = await api.post(`/trips/${tripId}/polls`, request);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to create poll');
  return data.pollId;
}

export async function castVote(
  tripId: string,
  pollId: string,
  request: CastVoteRequest
): Promise<void> {
  const response = await api.post(`/trips/${tripId}/polls/${pollId}/vote`, request);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to cast vote');
}

export async function removeVote(tripId: string, pollId: string): Promise<void> {
  const response = await api.delete(`/trips/${tripId}/polls/${pollId}/vote`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to remove vote');
}

export async function closePoll(tripId: string, pollId: string): Promise<string | null> {
  const response = await api.put(`/trips/${tripId}/polls/${pollId}/close`, {});
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to close poll');
  return data.winner || null;
}

export async function deletePoll(tripId: string, pollId: string): Promise<void> {
  const response = await api.delete(`/trips/${tripId}/polls/${pollId}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to delete poll');
}

// ====================================
// Activity Feed
// ====================================

export async function getActivity(
  tripId: string,
  options?: { limit?: number; before?: string }
): Promise<{ activities: TripActivity[]; hasMore: boolean; cursor?: string }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.before) params.set('before', options.before);

  const endpoint = `/trips/${tripId}/activity${params.toString() ? '?' + params : ''}`;
  const response = await api.get(endpoint);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch activity');
  return { activities: data.activities, hasMore: data.hasMore, cursor: data.cursor };
}

// ====================================
// Member Opt-outs
// ====================================

export async function getMemberOptouts(tripId: string, userId: string): Promise<MemberOptout[]> {
  const response = await api.get(`/trips/${tripId}/members/${userId}/optouts`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch opt-outs');
  return data.optouts;
}

export async function updateMemberOptouts(
  tripId: string,
  userId: string,
  optouts: { itemType: string; itemId?: string; reason?: string }[]
): Promise<void> {
  const response = await api.put(`/trips/${tripId}/members/${userId}/optouts`, { optouts });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to update opt-outs');
}

// ====================================
// Cost Split
// ====================================

export async function getCostSplit(tripId: string): Promise<{
  totalCost: number;
  memberCount: number;
  members: Array<{
    userId: string;
    displayName: string;
    avatarUrl?: string;
    items: Array<{
      itemType: string;
      itemName: string;
      amount: number;
      optedOut: boolean;
    }>;
    totalOwed: number;
    totalPaid: number;
    balance: number;
  }>;
}> {
  const response = await api.get(`/trips/${tripId}/split`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to fetch cost split');
  return data.split;
}

// ====================================
// Payments
// ====================================

export async function recordPayment(
  tripId: string,
  memberId: string,
  amount: number,
  method: string,
  notes?: string
): Promise<void> {
  const response = await api.post(`/trips/${tripId}/members/${memberId}/payment`, { amount, method, notes });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to record payment');
}

export async function requestPayment(tripId: string, memberIds?: string[]): Promise<void> {
  const response = await api.post(`/trips/${tripId}/request-payment`, { memberIds });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Failed to request payment');
}
